import React, { useState, useEffect } from "react";
import axios from "axios";
import { Line, Bar, Doughnut, Pie } from "react-chartjs-2";
import "chart.js/auto";
import dataEn from "../dataEn";
import dataKin from "../dataKin";
import { language } from "../tools/language";
import API_BASE_URL from "../config";

const data = language === "en" ? dataEn : dataKin;

function Index() {
  const [christians, setChristians] = useState([]);
  const [maleCount, setMaleCount] = useState(0);
  const [femaleCount, setFemaleCount] = useState(0);
  const [singleAdultCount, setSingleAdultCount] = useState(0);
  const [childCount, setChildCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [temporaryCount, setTemporaryCount] = useState(0);
  const [nolongerCount, setNolongerCount] = useState(0);
  const [punishedCount, setPunishedCount] = useState(0);
  const [groupCounts, setGroupCounts] = useState({});
  const [choirCounts, setChoirCounts] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/christian`)
      .then((response) => {
        let maleCount = 0;
        let femaleCount = 0;
        let singleAdultCount = 0;
        let activeCount = 0;
        let inactiveCount = 0;
        let temporaryCount = 0;
        let nolongerCount = 0;
        let punishedCount = 0;

        const groupCounter = {
          Narada: 0,
          Byiringiro: 0,
          Reverence: 0,
          "Fruta Melody": 0,
          Protocol: 0,
          Technic: 0,
          Umunyamasengesho: 0,
          Umuvugabutumwa: 0,
          Umujyanama: 0,
          "Umukirisitu usanzwe": 0,
        };
        
        const choirCounter = {
          Kicukiro: 0,
          Kimironko: 0,
          "Gikondo-Kigarama": 0,
          "Kabeza-Kanombe": 0,
          "Kagugu-Batsinda": 0,
          Kagina: 0,
          "Kagarama-Niboye": 0,
          Antenne: 0,
          Remera: 0,
          "Gatsata-Bweramvura": 0,
          Nyabisindu: 0,
          Rwanko: 0,
          Nyarugenge: 0,
          Gatenga: 0,
          Kabuga: 0,
          Kimisagara: 0,
          Rwabutenge: 0,
          Karembure: 0,
          Nyakabungo: 0,
          Nyirakanamba: 0,
          Nyabyunyu: 0,
          Gatovu: 0,
          Kiyanja: 0,
          Kagasa: 0,
          "Ntatsinda ubarizwamo": 0,
        };
        
        response.data.forEach((christian) => {
          if (christian.marital_status === "Single") {
            singleAdultCount++;
          } else {
            if ((christian.sex === "Male" || christian.sex === "Gabo" || christian.sex === "gabo") && christian.marital_status !== "Single") {
              maleCount++;
            } else if ((christian.sex === "Female" || christian.sex === "Gore" || christian.sex === "gore") && christian.marital_status !== "Single") {
              femaleCount++;
            }
          }

          if (groupCounter[christian.choir_group] !== undefined) {
            groupCounter[christian.choir_group]++;
          }

          if (typeof christian.church_roles === 'string') {
            const roles = christian.church_roles.split(',').map(role => role.trim());
            roles.forEach(role => {
              if (groupCounter[role] !== undefined) {
                groupCounter[role]++;
              }
            });
          } else if (Array.isArray(christian.church_roles)) {
            christian.church_roles.forEach(role => {
              if (groupCounter[role] !== undefined) {
                groupCounter[role]++;
              }
            });
          }

          if (choirCounter[christian.choir_member] !== undefined) {
            choirCounter[christian.choir_member]++;
          }
          
          switch (christian.status) {
            case "Active":
              activeCount++;
              break;
            case "Inactive":
              inactiveCount++;
              break;
            case "Temporary":
              temporaryCount++;
              break;
            case "Nolonger":
              nolongerCount++;
              break;
            case "Punished":
              punishedCount++;
              break;
            default:
              break;
          }
        });

        setGroupCounts(groupCounter);
        setChoirCounts(choirCounter);
        setMaleCount(maleCount);
        setFemaleCount(femaleCount);
        setSingleAdultCount(singleAdultCount);
        setActiveCount(activeCount);
        setInactiveCount(inactiveCount);
        setTemporaryCount(temporaryCount);
        setNolongerCount(nolongerCount);
        setPunishedCount(punishedCount);
        setChristians(response.data);
      })
      .catch((error) => {
        console.error("There was an error fetching the data!", error);
      });
  }, []);

  const groupChartData = {
    labels: [
      "Narada",
      "Byiringiro",
      "Reverence",
      "Fruta Melody",
      "Protocol",
      "Technical Team",
      "Abanyamasengesho",
      "Abavugabutumwa",
      "Abajyanama",
      "Abakirisitu basanzwe",
    ],
    datasets: [
      {
        label: "Statistics",
        data: Object.values(groupCounts),
        backgroundColor: ["#164e63", "#20E9FF", "#059AFD", "#164e63", "#20E9FF", "#059AFD", "#164e63", "#20E9FF", "#059AFD", "#164e63"],
        borderWidth: 1,
      },
    ],
  };

  const choirChartData = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12,
          },
          boxWidth: isMobile ? 10 : 40,
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw}`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: isMobile ? 90 : 45,
          minRotation: isMobile ? 45 : 45,
          font: {
            size: isMobile ? 8 : 10,
          },
          autoSkip: true,
          maxTicksLimit: isMobile ? 8 : 20,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: isMobile ? 10 : 12,
          },
        },
      },
    },
    labels: Object.keys(choirCounts),
    datasets: [
      {
        label: "Evangelical Cell Statistics",
        data: Object.values(choirCounts),
        backgroundColor: [
          "#164e63", "#6CB781", "#059AFD", "#F2C6C6", "#FF8C42", "#A4B5E0", "#B37BA4", "#6CBEE0", "#FA8072", "#90EE90",
          "#8A2BE2", "#5F9EA0", "#FF4500", "#2E8B57", "#6A5ACD", "#4682B4", "#D2691E", "#FFD700", "#87CEEB", "#00FA9A",
          "#B8860B", "#9932CC", "#C71585", "#FF1493", "#8B0000"
        ],
        borderWidth: 1,
      },
    ],
  };

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/child`)
      .then((response) => {
        const totalChild = response.data.length;
        setChildCount(totalChild);
      })
      .catch((error) => {
        console.error("There was an error fetching the data!", error);
      });
  }, []);

  const barChartData = {
    labels: ["Male", "Female", "Youth", "Children"],
    datasets: [
      {
        label: "Christian",
        data: [maleCount, femaleCount, singleAdultCount, childCount],
        backgroundColor: ["#6CB781", "#20E9FF", "#059AFD", "#164E63"],
        borderWidth: 1,
      },
    ],
  };

  const doughnutChartData = {
    labels: ["Active", "Inactive", "Visitors", "No longer", "Punished"],
    datasets: [
      {
        label: "Status",
        data: [activeCount, inactiveCount, temporaryCount, nolongerCount, punishedCount],
        backgroundColor: ["#6CB781", "#20E9FF", "#059AFD", "red", "#F2C6C6"],
      },
    ],
  };

  // Mobile-optimized chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12,
          },
          boxWidth: isMobile ? 10 : 40,
        },
      },
    },
  };

  return (
    <div className="content px-3 sm:px-5 md:px-8">
      {/* Header Section */}
      <div className="intro-y flex flex-col sm:flex-row items-center justify-between mt-5 mb-6">
        <h2 className="text-lg sm:text-xl font-medium text-[#164e63]">
          Church Management Dashboard
        </h2>
        <div className="text-xs sm:text-sm text-gray-500 mt-2 sm:mt-0">
          {/* Last updated: {new Date().toLocaleDateString()} */}
        </div>
      </div>

      <div className="intro-y grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-3">
        {/* Left Column */}
        <div className="col-span-1">
          {/* Church Members Card */}
          <div className="intro-y box mt-3 sm:mt-5 overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center p-3 sm:p-5 border-b border-slate-200/60">
              <h2 className="font-medium text-sm sm:text-base mr-auto">Church Members</h2>
            </div>
            
            <div className="p-3 sm:p-5">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                {/* Statistics Section */}
                <div className="w-full md:w-1/2 space-y-2 sm:space-y-3">
                  <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                    <span className="text-[#164e63] font-bold text-base sm:text-xl">Adult Members:</span>
                    <span className="text-base sm:text-xl font-bold text-[#164e63]">{maleCount + femaleCount + singleAdultCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#164e63] text-sm sm:text-md"> Male:</span>
                    <span className="text-sm sm:text-lg font-semibold text-[#164e63]">{maleCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#164e63] text-sm sm:text-md"> Female:</span>
                    <span className="text-sm sm:text-lg font-semibold text-[#164e63]">{femaleCount}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#164e63] text-sm sm:text-md"> Youth:</span>
                    <span className="text-sm sm:text-lg font-bold text-[#164e63]">{singleAdultCount}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-gray-200 pt-2 pb-2">
                    <span className="text-[#164e63] font-bold text-base sm:text-xl">Children Members:</span>
                    <span className="text-base sm:text-xl font-bold text-[#164e63]">{childCount}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <b className="text-[#d6702c] font-bold text-lg sm:text-xl">Total Members:</b>
                    <span className="text-xl sm:text-2xl font-bold text-[#d6702c]">{maleCount + femaleCount + singleAdultCount + childCount}</span>
                  </div>
                </div>

                {/* Chart Section */}
                <div className="w-full md:w-1/2">
                  <div className="h-[250px] sm:h-[300px]">
                    <Doughnut data={barChartData} options={chartOptions} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Evangelical Cell Statistics Card */}
          <div className="intro-y box mt-3 sm:mt-5 overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center p-3 sm:p-5 border-b border-slate-200/60">
              <h2 className="font-medium text-sm sm:text-base mr-auto">Evangelical Cell Statistics</h2>
            </div>
            <div className="p-3 sm:p-5">
              <div className="h-[350px] sm:h-[400px] md:h-[366px]">
                <Bar 
                  data={choirChartData} 
                  options={{
                    ...chartOptions,
                    maintainAspectRatio: false,
                    responsive: true,
                  }} 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="col-span-1">
          {/* Statistics by Ministry Card */}
          <div className="intro-y box mt-3 sm:mt-5 overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center p-3 sm:p-5 border-b border-slate-200/60">
              <h2 className="font-medium text-sm sm:text-base mr-auto">Statistics by Ministry</h2>
            </div>
            <div className="p-3 sm:p-5">
              <div className="h-[350px] sm:h-[400px] md:h-[300px]">
                <Bar 
                  data={groupChartData} 
                  options={{
                    ...chartOptions,
                    maintainAspectRatio: false,
                    responsive: true,
                    scales: {
                      x: {
                        ticks: {
                          maxRotation: isMobile ? 45 : 45,
                          minRotation: isMobile ? 45 : 45,
                          font: {
                            size: isMobile ? 9 : 11,
                          },
                          autoSkip: true,
                          maxTicksLimit: isMobile ? 6 : 10,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        ticks: {
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                    },
                  }} 
                />
              </div>
            </div>
          </div>
          
          {/* Member Status Card */}
          <div className="intro-y box mt-3 sm:mt-5 overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center p-3 sm:p-5 border-b border-slate-200/60">
              <h2 className="font-medium text-sm sm:text-base mr-auto">Member Status</h2>
            </div>
            <div className="p-3 sm:p-5">
              <div className="flex justify-center items-center">
                <div className="h-[250px] sm:h-[300px] w-full">
                  <Doughnut data={doughnutChartData} options={chartOptions} />
                </div>
              </div>
              {/* Status Legend for Mobile */}
              <div className="grid grid-cols-2 gap-2 mt-4 sm:hidden">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-[#6CB781]"></div>
                  <span className="text-xs">Active: {activeCount}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-[#20E9FF]"></div>
                  <span className="text-xs">Inactive: {inactiveCount}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-[#059AFD]"></div>
                  <span className="text-xs">Visitors: {temporaryCount}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-xs">No longer: {nolongerCount}</span>
                </div>
                <div className="flex items-center space-x-2 col-span-2">
                  <div className="w-3 h-3 rounded-full bg-[#F2C6C6]"></div>
                  <span className="text-xs">Punished: {punishedCount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Index;